import { C, mono } from '../theme';
import { inr, fmtDate } from '../format';
import type { RegisterEntry } from '../register';

/**
 * The running-balance register — Date · Description · Charge · Payment · Balance.
 * One presentation shared by the owner detail, mobile detail and tenant statement
 * (tenant is simply read-only). Scrolls horizontally on narrow screens; `compact`
 * tightens padding/type for the phone frame.
 */
export function Register({
  entries,
  compact = false,
}: {
  entries: RegisterEntry[];
  compact?: boolean;
}) {
  const padX = compact ? 9 : 12;
  const padY = compact ? 9 : 11;
  const fs = compact ? 12.2 : 13.3;

  const th: React.CSSProperties = {
    background: C.bg,
    fontSize: compact ? 9.8 : 10.6,
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    color: C.muted,
    fontWeight: 600,
    padding: `9px ${padX}px`,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: fs,
          background: C.card,
          border: `1px solid ${C.line}`,
          borderRadius: 11,
          overflow: 'hidden',
          minWidth: compact ? 460 : 0,
        }}
      >
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Date</th>
            <th style={{ ...th, textAlign: 'left' }}>Description</th>
            <th style={{ ...th, textAlign: 'right' }}>Charge</th>
            <th style={{ ...th, textAlign: 'right' }}>Payment</th>
            <th style={{ ...th, textAlign: 'right' }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const opening = e.kind === 'opening';
            const creditColor = e.kind === 'tds' ? C.red : C.hi;
            const rowBg = opening ? C.bg : e.kind === 'payment' ? C.greenSoft : 'transparent';
            return (
              <tr key={e.key} style={{ background: rowBg }}>
                <td
                  style={{
                    padding: `${padY}px ${padX}px`,
                    textAlign: 'left',
                    borderTop: i === 0 ? 0 : `1px solid ${C.line2}`,
                    color: C.muted,
                    whiteSpace: 'nowrap',
                    ...mono({ fontSize: compact ? 11 : 11.8 }),
                  }}
                >
                  {e.date != null ? fmtDate(e.date) : '–'}
                </td>
                <td
                  style={{
                    padding: `${padY}px ${padX}px`,
                    textAlign: 'left',
                    borderTop: i === 0 ? 0 : `1px solid ${C.line2}`,
                  }}
                >
                  <span style={{ fontWeight: 600, color: opening ? C.ink2 : C.ink }}>{e.description}</span>
                  {e.sub && (
                    <small style={{ display: 'block', color: C.muted, fontSize: compact ? 10.6 : 11.4, fontWeight: 400 }}>{e.sub}</small>
                  )}
                </td>
                <td style={{ padding: `${padY}px ${padX}px`, textAlign: 'right', borderTop: i === 0 ? 0 : `1px solid ${C.line2}`, ...mono({ color: C.ink }) }}>
                  {e.charge > 0 ? inr(e.charge) : ''}
                </td>
                <td style={{ padding: `${padY}px ${padX}px`, textAlign: 'right', borderTop: i === 0 ? 0 : `1px solid ${C.line2}`, ...mono({ color: creditColor }) }}>
                  {e.credit > 0 ? `−${inr(e.credit)}` : ''}
                </td>
                <td style={{ padding: `${padY}px ${padX}px`, textAlign: 'right', borderTop: i === 0 ? 0 : `1px solid ${C.line2}`, ...mono({ color: e.balance > 0 ? C.red : C.hi, fontWeight: 600 }) }}>
                  {inr(e.balance)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
